// Angular imports
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

// 3rd party imports
import { DxValidationGroupComponent } from 'devextreme-angular';

// Component imports
import { IconConstants } from '@therealmanage/ciranet-ui-common';
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// Service imports
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { UserService } from '../../_services/data-services/user.service';

// Model imports
import { CreateUserParameterDto } from '../../_models/create-user-parameter-dto';
import { RegistrationEditorParametersDto } from '../../_models/registration/registration-editor-parameters-dto';
import { RegisterWithoutRecaptchaParametersDto } from '../../_models/registration/register-without-recaptcha-parameters-dto';
import { RegistrationEditorResultDto } from '../../_models/registration/registration-editor-result-dto';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Constant imports
import { ImageConstants } from '../../_constants/image-constants';

@Component({
  selector: 'app-register-account',
  templateUrl: './register-account.component.html',
  styleUrls: ['./register-account.component.scss'],
  standalone: false
})
export class RegisterAccountComponent
  extends RecaptchaBaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('registerAccountValidationGroup', { static: false })
  registerAccountValidationGroup: DxValidationGroupComponent;

  @Input() isEmbedded = false;
  @Output() closePanel = new EventEmitter<void>();

  isSmallScreen = false;
  loadingVisible = false;
  userInfo: CreateUserParameterDto = new CreateUserParameterDto();
  showRegisterDetails = false;
  showSignupAccountName = false;
  showEmailOptions = false;
  showTextOptions = false;
  registrationEditor: RegistrationEditorResultDto | null;
  showErrorMessage = false;
  errorMessage = '';
  showMaxUsersReached = false;
  showCommunityInactive = false;
  isGoogleSignup = false;
  isAccountInfoShow = false;
  helpImage: string;
  private subscriptions = new Subscription();

  helpIcon = IconConstants.HELP;

  private readonly GOOGLE_REGISTRATION_RESULT_KEY =
    'google_registration_result';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private screenService: ScreenService,
    private userService: UserService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService
  ) {
    super(errorService, envConfig);
  }

  ngOnInit(): void {
    this.helpImage = ImageConstants.helpAccountImage;
    this.updateScreenSize();
    this.subscriptions.add(
      RxjsUtils.safeSubscribe(this.screenService.changed, () =>
        this.updateScreenSize()
      )
    );

    // Check if returning from Google OAuth
    const fromGoogle = this.route.snapshot.queryParams['fromGoogle'];
    if (fromGoogle === 'true') {
      this.handleGoogleRegistration();
    }
  }

  private handleGoogleRegistration(): void {
    const registrationData = sessionStorage.getItem(
      this.GOOGLE_REGISTRATION_RESULT_KEY
    );

    if (!registrationData) {
      this.isGoogleSignup = false;
      this.messageBox.showErrorMessage(
        'Error',
        'Registration data not found. Please start again.'
      );
      return;
    }

    let data: any;
    try {
      data = JSON.parse(registrationData);
    } catch {
      this.isGoogleSignup = false;
      sessionStorage.removeItem(this.GOOGLE_REGISTRATION_RESULT_KEY);
      this.messageBox.showErrorMessage(
        'Error',
        'Registration data is corrupted. Please start again.'
      );
      return;
    }

    this.isGoogleSignup = true;

    // Populate userInfo with Google data
    this.userInfo.email = data.email;
    this.userInfo.password = data.generatedPassword;
    this.userInfo.customerID = data.customerID;
    this.userInfo.checkDigit = data.checkDigit;
    this.userInfo.recaptchaToken = data.recaptchaToken;

    // Restore contactInfo if owner was already selected before Google redirect
    if (data.contactInfo) {
      this.userInfo.contactInfo = data.contactInfo;
    }

    // Cleanup sessionStorage
    sessionStorage.removeItem(this.GOOGLE_REGISTRATION_RESULT_KEY);

    // Call registration API
    this.callRegistrationApi(!!data.contactInfo);
  }

  private callRegistrationApi(skipOwnerSelection: boolean = false): void {
    const params = new RegistrationEditorParametersDto();
    params.checkDigit = this.userInfo.checkDigit;
    params.customerId = this.userInfo.customerID;
    params.email = this.userInfo.email;
    params.recaptchaToken = this.userInfo.recaptchaToken;

    this.loadingVisible = true;

    RxjsUtils.safeSubscribe(
      this.userService.getUserRegistrationEditor(params),
      (result: RegistrationEditorResultDto) => {
        this.loadingVisible = false;
        this.registrationEditor = result;

        if (skipOwnerSelection) {
          // Owner was already selected before Google redirect, skip to Email Options
          this.showEmailOptions = true;
        } else {
          // Show owner selection step
          this.showSignupAccountName = true;
        }
      },
      (error) => {
        this.loadingVisible = false;
        this.resetGoogleSignupState();
        this.processApiError(error);
      }
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  btnContinueClick(event: any): void {
    // Clear any previous error message
    this.showErrorMessage = false;
    this.errorMessage = '';

    // Reset Google signup flag - user is choosing normal flow
    this.isGoogleSignup = false;

    const validator = this.registerAccountValidationGroup.instance.validate();
    if (validator.isValid) {
      // Fetch registration editor data before showing signup account name
      this.fetchRegistrationEditor();
    }
  }

  private fetchRegistrationEditor(): void {
    const params = new RegisterWithoutRecaptchaParametersDto();
    params.checkDigit = this.userInfo.checkDigit;
    params.customerId = this.userInfo.customerID;

    this.loadingVisible = true;

    RxjsUtils.safeSubscribe(
      this.userService.validateNewUserAccountWithoutRecaptcha(params),
      (result: RegistrationEditorResultDto) => {
        this.loadingVisible = false;
        this.registrationEditor = result;
        // Show the signup account name form (user selection)
        this.showSignupAccountName = true;
      },
      (error) => {
        this.loadingVisible = false;
        this.processApiError(error);
      }
    );
  }

  onBackFromDetails(): void {
    // Go back to signup account name (user selection)
    this.showRegisterDetails = false;
    this.showSignupAccountName = true;
  }

  onRegistrationUpdate(event: any): void {
    this.userInfo = event.userInfo;
    // Show the email options form (after register details)
    this.showEmailOptions = true;
  }

  onBackFromSignup(): void {
    // Go back to account number entry
    this.showSignupAccountName = false;
  }

  onPortalAccountUpdate(userInfo: CreateUserParameterDto): void {
    this.userInfo = userInfo;

    if (this.isGoogleSignup) {
      // For Google sign-up, skip Register Details and go directly to Email Options
      this.showSignupAccountName = false;
      this.showEmailOptions = true;
    } else {
      // Normal flow: show Register Details form (email/password)
      this.showRegisterDetails = true;
    }
  }

  onBackFromEmailOptions(): void {
    this.showEmailOptions = false;
  }

  onEmailCommunicationUpdate(userInfo: CreateUserParameterDto): void {
    this.userInfo = userInfo;
    // Show the text options form
    this.showTextOptions = true;
  }

  onBackFromTextOptions(): void {
    this.showTextOptions = false;
  }

  onCloseRegisterPanel(): void {
    this.resetGoogleSignupState();
    this.closePanel.emit();
  }

  btnBackToSignIn(event: any): void {
    this.resetGoogleSignupState();
    if (this.isEmbedded) {
      this.closePanel.emit();
    } else {
      this.router.navigate(['user', 'login']);
    }
  }

  private resetGoogleSignupState(): void {
    if (this.isGoogleSignup) {
      this.userInfo.email = '';
      this.userInfo.password = '';
    }
    this.isGoogleSignup = false;
  }

  btnShowHelp(): void {
    this.isAccountInfoShow = true;
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }

  // Override to show inline error instead of popup
  override processApiError(error: any): void {
    const apiError = error.error;
    let message = 'An error occurred. Please try again.';

    if (apiError) {
      // Check for modelState errors (ASP.NET Web API format)
      if (apiError.modelState) {
        const errorMessages = Object.values(apiError.modelState).flat();
        if (errorMessages.length > 0) {
          message = (errorMessages as string[]).join(' ');
        }
      } else if (apiError.errors) {
        // Handle validation errors object
        const errorMessages = Object.values(apiError.errors).flat();
        if (errorMessages.length > 0) {
          message = (errorMessages as string[]).join(' ');
        }
      } else if (apiError.Message) {
        message = apiError.Message;
      } else if (apiError.message) {
        message = apiError.message;
      } else if (typeof apiError === 'string') {
        message = apiError;
      }
    }

    const lowerMessage = message.toLowerCase();

    // Check for max users reached - show dedicated screen
    if (
      lowerMessage.includes('registered users') ||
      lowerMessage.includes('maximum number of allowed users')
    ) {
      this.showMaxUsersReached = true;
      return;
    }

    // Check for community inactive/terminated - show dedicated screen
    if (
      lowerMessage.includes('no longer managed') ||
      lowerMessage.includes('terminated')
    ) {
      this.showCommunityInactive = true;
      return;
    }

    // Replace unfriendly error messages with user-friendly versions
    if (lowerMessage.includes('customer id is not valid')) {
      message =
        "Sorry, we couldn't find your account. Please try again or contact customer support.";
    }

    this.showErrorMessage = true;
    this.errorMessage = message;
  }

  btnForgotPassword(): void {
    this.router.navigate(['user', 'forgotpassword']);
  }

  btnForgotUsername(): void {
    this.router.navigate(['user', 'forgotusername']);
  }
}
